import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import AuthCallback from './components/AuthCallback';
import LoginPage from './pages/LoginPage';
import SetPassword from './pages/SetPassword';
import Dashboard from './pages/Dashboard';
import BudgetUpload from './pages/BudgetUpload';
import PurchaseOrders from './pages/PurchaseOrders';
import PODetail from './pages/PODetail';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import Vendors from './pages/Vendors';
import VendorDetail from './pages/VendorDetail';
import Bank from './pages/Bank';
import Approvals from './pages/Approvals';
import ApprovalDetail from './pages/ApprovalDetail';
import Revenue from './pages/Revenue';
import AIAssistant from './pages/AIAssistant';
import EmailHub from './pages/EmailHub';
import Accounting from './pages/Accounting';
import Reports from './pages/Reports';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import BudgetHeadDetail from './pages/BudgetHeadDetail';
import UserManagement from './pages/UserManagement';
import TournamentManagement from './pages/TournamentManagement';
import Divisions from './pages/Divisions';
import GrantClaims from './pages/GrantClaims';
import GrantPolicyDetail from './pages/GrantPolicyDetail';
import AdminCleanup from './pages/AdminCleanup';
import LiveMonitor from './pages/LiveMonitor';
import PortalLayout from './pages/portal/PortalLayout';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalClaims from './pages/portal/PortalClaims';
import PortalClaimWizard from './pages/portal/PortalClaimWizard';
import PortalKYC from './pages/portal/PortalKYC';
import CaseStudy from './pages/CaseStudy';
import CaseStudyReel from './pages/CaseStudyReel';
import Storyline from './pages/Storyline';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import '@/App.css';

// Router component to handle session_id detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPassword />} />

      {/* Public shareable case-study artefact — no auth */}
      <Route path="/case-study" element={<CaseStudy />} />
      <Route path="/reel" element={<CaseStudyReel />} />
      <Route path="/storyline" element={<Storyline />} />

      {/* Division Portal — secretary-only, isolated layout */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PortalDashboard />} />
        <Route path="claims" element={<PortalClaims />} />
        <Route path="claim-wizard/:claimId" element={<PortalClaimWizard />} />
        <Route path="kyc" element={<PortalKYC />} />
        <Route path="policies/:id" element={<GrantPolicyDetail mode="secretary" />} />
      </Route>

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget-upload"
        element={
          <ProtectedRoute requiredPermission="budget_upload">
            <Layout>
              <BudgetUpload />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute requiredPermission="purchase_orders">
            <Layout>
              <PurchaseOrders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders/:poId"
        element={
          <ProtectedRoute requiredPermission="purchase_orders">
            <Layout>
              <PODetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute requiredPermission="invoices">
            <Layout>
              <Invoices />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:invoiceId"
        element={
          <ProtectedRoute requiredPermission="invoices">
            <Layout>
              <InvoiceDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <ProtectedRoute requiredPermission="vendors">
            <Layout>
              <Vendors />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <ProtectedRoute requiredPermission="vendors">
            <Layout>
              <VendorDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank"
        element={
          <ProtectedRoute requiredPermission="bank">
            <Layout>
              <Bank />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <ProtectedRoute requiredPermission="approvals">
            <Layout>
              <Approvals />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals/:id"
        element={
          <ProtectedRoute requiredPermission="approvals">
            <Layout>
              <ApprovalDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/revenue"
        element={
          <ProtectedRoute requiredPermission="revenue">
            <Layout>
              <Revenue />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai"
        element={
          <ProtectedRoute requiredPermission="ai">
            <Layout>
              <AIAssistant />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-hub"
        element={
          <ProtectedRoute requiredPermission="email_hub">
            <Layout>
              <EmailHub />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounting"
        element={
          <ProtectedRoute requiredPermission="accounting">
            <Layout>
              <Accounting />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute requiredPermission="reports">
            <Layout>
              <Reports />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute requiredPermission="alerts">
            <Layout>
              <Alerts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredPermission="settings">
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Layout>
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tournaments"
        element={
          <ProtectedRoute>
            <Layout>
              <TournamentManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/divisions"
        element={
          <ProtectedRoute>
            <Layout>
              <Divisions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grant-claims"
        element={
          <ProtectedRoute>
            <Layout>
              <GrantClaims />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grant-policies/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <GrantPolicyDetail mode="admin" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget-head/:budgetHeadId"
        element={
          <ProtectedRoute>
            <BudgetHeadDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cleanup"
        element={
          <ProtectedRoute>
            <Layout>
              <AdminCleanup />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/monitor"
        element={
          <ProtectedRoute>
            <Layout>
              <LiveMonitor />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
